import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Typography,
  AppBar,
  Stack,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Autocomplete,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  CustomTitle,
  getFilteredData,
  Loader,
} from "../HelperFunctions/HelperFunctions";
import { ResCorporationPcsByMgmtGroup } from "../API/nimbleAPI";
import { forwardRef, useEffect, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { enqueueSnackbar } from "notistack";

const columns = [
  {
    name: "corporation_name",
    dataKey: "CorporationName",
    width: "120px",
  },
  { name: "legal_name", dataKey: "LegalName", width: "120px" },
  { name: "brand", dataKey: "Brand", width: "65px" },
  { name: "pms", dataKey: "PMS", width: "80px" },
];

const HEADER_PREFIX = "--";

const propertyTypeLabel = (type) => {
  if (type === 1 || type === "1") return "Operating";
  if (type === 2 || type === "2") return "Non-Operating";
  return type || "-";
};

export const normalizeRestaurantCorpRows = (data) => {
  const list = Array.isArray(data) ? data : data?.Corporations || [];
  return list.map((corp) => ({
    ...corp,
    CorporationID: String(corp.CorporationID ?? corp.ID ?? ""),
    CorporationName: corp.CorporationName ?? corp.Name ?? "-",
    LegalName: corp.LegalName ?? "-",
    PropertyType: propertyTypeLabel(corp.PropertyType),
    Service: corp.Service ?? "-",
    Brand: corp.Brand ?? "-",
    PMS: corp.PMS ?? "-",
  }));
};
const normalizeGroups = (restPortifolios) => {
  const list = Array.isArray(restPortifolios)
    ? restPortifolios
    : restPortifolios?.Groups || [];

  return list.map((item) => ({
    ...item,
    ValueCode: item.ValueCode ?? item.Key ?? item.GroupID ?? item.ID ?? "",
    DisplayText:
      item.DisplayText || item.Value || item.Name || item.GroupName || "",
  }));
};

const isAllGroup = (option) =>
  !option ||
  option.DisplayText === "-All-" ||
  option.DisplayText === "--ALL--" ||
  option.ValueCode === "ALL" ||
  option.ValueCode === "All";

const isHeaderGroup = (option) =>
  typeof option?.DisplayText === "string" &&
  option.DisplayText.startsWith(HEADER_PREFIX);

const TableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      elevation={0}
      sx={{ height: "100%", overflow: "auto" }}
    />
  )),
  Table: (props) => (
    <Table
      {...props}
      stickyHeader
      style={{
        borderCollapse: "separate",
        tableLayout: "fixed",
      }}
    />
  ),
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow: ({ item, context, ...props }) => <TableRow {...props} hover />,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

const RestaurantCorpSelectDialog = ({
  open,
  onClose,
  corporationData = [],
  selectedCorporations = [],
  restPortifolios,
  onApply,
  corpCount,
}) => {
  const portfolioGroups = normalizeGroups(restPortifolios);

  const [loading, setLoading] = useState(false);
  const [corpTableData, setCorpTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedManagementGroup, setSelectedManagementGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [corporationCount, setCorporationCount] = useState(0);

  const applyTableData = (rows) => {
    const normalized = normalizeRestaurantCorpRows(rows);
    setCorpTableData(normalized);
    setOriginalData(normalized);
    setCorporationCount(normalized.length);
  };

  useEffect(() => {
    if (!open) return;

    applyTableData(corporationData);
    setSelectedRows((selectedCorporations || []).map(String));
    const allOption =
      portfolioGroups.find((opt) => isAllGroup(opt) && !isHeaderGroup(opt)) ||
      null;
    setSelectedManagementGroup(allOption);
    setSearchTerm("");
  }, [open]);

  const handleApply = () => {
    onApply?.(selectedRows);
    corpCount?.(corporationCount);
    onClose();
  };

  const getCorporation = async (groupId) => {
    setLoading(true);
    try {
      const data = await ResCorporationPcsByMgmtGroup(groupId);
      applyTableData(data);
    } catch (error) {
      enqueueSnackbar("Error while getting corporation details", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGroupChange = (_event, newValue) => {
    setSelectedRows([]);
    setSearchTerm("");

    if (isHeaderGroup(newValue) || isAllGroup(newValue)) {
      const allOption =
        portfolioGroups.find((opt) => isAllGroup(opt) && !isHeaderGroup(opt)) ||
        null;
      setSelectedManagementGroup(allOption);
      applyTableData(corporationData);
      return;
    }

    setSelectedManagementGroup(newValue);
    if (newValue?.ValueCode) {
      getCorporation(newValue.ValueCode);
    }
  };

  const toggleRow = (corporationId) => {
    if (!corporationId) return;
    const id = String(corporationId);
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedRows(
      checked
        ? corpTableData.map((row) => String(row.CorporationID)).filter(Boolean)
        : [],
    );
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCorpTableData(getFilteredData(value, originalData));
  };

  const fixedHeaderContent = () => (
    <TableRow
      sx={{
        "& .MuiTableCell-root": {
          backgroundColor: "#d7d7d7 !important",
        },
      }}
    >
      <TableCell
        width="40px"
        sx={{
          padding: "1px",
          borderRight: "1px solid white",
          minWidth: "30px",
          maxWidth: "30px",
        }}
      >
        <Checkbox
          size="small"
          color="grey"
          disableRipple
          checked={
            corpTableData.length > 0 &&
            selectedRows.length === corpTableData.length
          }
          indeterminate={
            selectedRows.length > 0 &&
            selectedRows.length < corpTableData.length
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      </TableCell>
      {columns.map((column, index) => (
        <TableCell
          key={index}
          width={column.width}
          sx={{
            borderRight: "1px solid white",
            padding: 1,
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          {column.name === "pms" ? "PMS" : CustomTitle(column.name)}
        </TableCell>
      ))}
    </TableRow>
  );

  const itemContent = (index, row, context) => {
    const selected = context || [];
    const rowId = row?.CorporationID != null ? String(row.CorporationID) : "";
    const isChecked = selected.includes(rowId);
    return (
      <>
        <TableCell sx={{ padding: "1px", borderRight: "1px solid #f2f2f2" }}>
          <Checkbox
            size="small"
            color="grey"
            disableRipple
            checked={isChecked}
            onChange={() => toggleRow(rowId)}
          />
        </TableCell>
        {columns.map((column, colIndex) => (
          <TableCell
            key={colIndex}
            sx={{
              borderRight: "1px solid #f2f2f2",
              padding: "10px",
              fontSize: "12px",
              maxWidth: column.width,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Tooltip title={row?.[column.dataKey] || "-"} arrow>
              <span>{row?.[column.dataKey] || "-"}</span>
            </Tooltip>
          </TableCell>
        ))}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      fullScreen
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      hideBackdrop
      sx={{
        zIndex: 2000,
        pointerEvents: "auto",
        "& .MuiDialog-paper": { pointerEvents: "auto" },
      }}
    >
      <AppBar
        sx={{ position: "relative", bgcolor: "#d7d7d7", height: 45 }}
        elevation={0}
      >
        <Box sx={{ height: 45, alignContent: "center", padding: "0 15px" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row">
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                onClick={onClose}
                aria-label="close"
                style={{ backgroundColor: "#686868" }}
                disableRipple
              >
                <CloseIcon sx={{ fontSize: "15px" }} />
              </IconButton>
              <Typography
                sx={{
                  ml: 2,
                  flexGrow: 10,
                  color: "#1f1f1f",
                }}
                component="div"
                fontWeight={600}
                fontSize="15px"
              >
                Corporation Selection
              </Typography>
            </Stack>
            <Stack direction="row" gap={3}>
              <Button
                variant="contained"
                size="small"
                disableElevation
                disableRipple
                sx={{
                  px: 4,
                  textTransform: "none",
                  backgroundColor: "#b5dcf2",
                  color: "#000",
                  fontWeight: 400,
                }}
                onClick={handleApply}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </AppBar>
      <DialogContent sx={{ padding: 1, overflow: "hidden", display: "flex" }}>
        <Stack gap={2} height="100%" direction="column" flex={1} minHeight={0}>
          <Stack direction="row" gap={2}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 250, maxWidth: 250, m: 1 }}
              options={portfolioGroups}
              getOptionLabel={(option) => option?.DisplayText || ""}
              isOptionEqualToValue={(option, value) =>
                option?.ValueCode === value?.ValueCode &&
                option?.DisplayText === value?.DisplayText
              }
              value={selectedManagementGroup}
              disableClearable
              slotProps={{
                popper: { disablePortal: true, sx: { zIndex: 2000 } },
              }}
              onChange={handleGroupChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Management Groups"
                  inputProps={{ ...params.inputProps, readOnly: true }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { fontSize: "13px", fontWeight: 500 },
                  }}
                  InputLabelProps={{
                    sx: { fontSize: "12px" },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={`${option.ValueCode}-${option.DisplayText}`}
                  style={{
                    fontSize: "13px",
                    fontWeight: isHeaderGroup(option) ? 600 : 500,
                  }}
                >
                  {option.DisplayText}
                </li>
              )}
            />

            <TextField
              sx={{
                mt: 1,
                minWidth: 220,
                backgroundColor: "transparent",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "transparent",
                  maxHeight: "33px",
                  fontSize: "13px",
                  bgcolor: "#f5f5f5",
                  fontWeight: 500,
                  cursor: "pointer",
                },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                },
              }}
              size="small"
              label="Corporation"
              name="corporation"
              value={
                selectedRows.length === corporationCount && corporationCount > 0
                  ? "All_corporation(s)selected"
                  : selectedRows.length > 9
                    ? `${selectedRows.length}_corporation(s)selected`
                    : selectedRows.length > 0
                      ? `0${selectedRows.length}_corporation(s)selected`
                      : "00_corporation(s)selected"
              }
              InputProps={{ readOnly: true }}
            />

            <TextField
              size="small"
              placeholder="Search"
              sx={{
                mt: 1,
                minWidth: 300,
                backgroundColor: "transparent",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "transparent",
                  maxHeight: "33px",
                  fontSize: "13px",
                  bgcolor: "#f5f5f5",
                  fontWeight: 500,
                },
              }}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Stack>

          <Box flex={1} minHeight={0} border="1px solid #d7d7d7">
            <TableVirtuoso
              style={{ height: "100%" }}
              data={corpTableData}
              context={selectedRows}
              components={TableComponents}
              fixedHeaderContent={fixedHeaderContent}
              itemContent={itemContent}
              // computeItemKey={(index, item) => index}
              // increaseViewportBy={400}
            />
          </Box>
        </Stack>
      </DialogContent>
      <Loader loading={loading} />
    </Dialog>
  );
};

export default RestaurantCorpSelectDialog;
